import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Trophy, Shield, Users, Medal, TrendingUp } from 'lucide-react';
import { championshipApi } from '../lib/api';
import { PageContent } from '../components/layout/PageContent';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import SearchableCombobox from '../components/SearchableCombobox';
import { EstadoVazio } from '../components/common/Estados';

const PLACE_LABELS = { 1: '1º', 2: '2º', 3: '3º', 4: '4º' };

/**
 * Visual language shared with the rest of e-Torneios (see MedalsPage). Only
 * tokens that already have a light-theme counterpart in
 * index.css are used, so the page reads correctly in BOTH themes:
 *   - `text-white` / `text-[#A1A1AA]` / `text-[#D4AF37]` are remapped in light
 *   - `bg-[#141414]` / `bg-[#1A1A1A]` / `bg-[#0A0A0A]` are remapped in light
 *   - `text-black` is intentionally NOT remapped, so the medal pills keep
 *     black text on gold/silver/bronze in both themes.
 */
const ROW_TONE = {
  1: 'bg-gradient-to-r from-[#D4AF37]/20 to-transparent border border-[#D4AF37]/30',
  2: 'bg-gradient-to-r from-[#C0C0C0]/20 to-transparent border border-[#C0C0C0]/30',
  3: 'bg-gradient-to-r from-[#CD7F32]/20 to-transparent border border-[#CD7F32]/30',
};
const BADGE_TONE = {
  1: 'bg-[#D4AF37] text-black',
  2: 'bg-[#C0C0C0] text-black',
  3: 'bg-[#CD7F32] text-black',
};
const NEUTRAL_ROW = 'bg-[#1A1A1A] border border-[#27272A]';
const NEUTRAL_BADGE = 'bg-[#0A0A0A] text-white border border-[#27272A]';

const PLACE_TEXT = { 1: 'medalha-ouro', 2: 'medalha-prata', 3: 'medalha-bronze', 4: 'text-[#71717A]' };

const normalizeForSearch = (text) =>
  (text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** Round position badge used by both tabs. */
const PositionBadge = ({ place }) => {
  const pos = Number.isFinite(place) ? place : null;
  const label = pos ? `${pos}º lugar` : 'Sem colocação';
  return (
    <div
      title={label}
      aria-label={label}
      className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold text-lg ${
        (pos && BADGE_TONE[pos]) || NEUTRAL_BADGE
      }`}
    >
      {pos ?? '—'}
    </div>
  );
};

/** Right-hand points block of the ranking table. */
const PointsBlock = ({ points, label = 'pontos' }) => (
  <div className="text-right shrink-0">
    <div className="flex items-center gap-1 text-[#D4AF37]">
      <TrendingUp size={16} />
      <span className="text-2xl font-bold">{points}</span>
    </div>
    <p className="text-xs text-[#71717A]">{label}</p>
  </div>
);

/** Athlete + team line (preserves the Atleta and Equipe columns). */
const AthleteCell = ({ name, team }) => (
  <div className="flex-1 min-w-0">
    <h3 className="font-bold text-white truncate">{name}</h3>
    <div className="flex items-center gap-1.5 mt-1 text-sm text-[#A1A1AA] min-w-0">
      <Shield className="w-4 h-4 shrink-0 text-[#D4AF37]" />
      <span className="truncate">{team || 'Sem equipe'}</span>
    </div>
  </div>
);

/** Gold / Silver / Bronze counters (same icons and colors as MedalsPage). */
const MedalCounts = ({ gold, silver, bronze }) => (
  <div className="flex items-center gap-4 text-sm shrink-0">
    <span className="flex items-center gap-1">
      <Medal className="w-4 h-4 medalha-ouro" />
      <span className="text-white font-medium">{gold}</span>
    </span>
    <span className="flex items-center gap-1">
      <Medal className="w-4 h-4 medalha-prata" />
      <span className="text-white font-medium">{silver}</span>
    </span>
    <span className="flex items-center gap-1">
      <Medal className="w-4 h-4 medalha-bronze" />
      <span className="text-white font-medium">{bronze}</span>
    </span>
  </div>
);

/**
 * Ranking DO CAMPEONATO (rota /championships/:id/ranking).
 *
 * Escopada ao campeonato do `:id`, esta página lê as fontes de verdade
 * DO CAMPEONATO:
 *   GET /api/championships/{id}/live-ranking?modality=   (atletas)
 *   GET /api/championships/{id}/team-ranking?modality=   (equipes)
 *
 * Regras de negócio respeitadas:
 *   - somente os atletas/equipes DESTE campeonato;
 *   - modalidade (gi/nogi) quando o campeonato é MISTO;
 *   - `combine_modality_ranking === false` → separa por modalidade (começa em
 *     Kimono), igual à Mesa Central e ao Ranking Público;
 *   - a EQUIPE recebe somente pontos de medalha (o backend já entrega
 *     `total_points` composto só por medalha neste motor).
 */
export default function ChampionshipRankingPage() {
  const { id } = useParams();
  const [championship, setChampionship] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [rankingModality, setRankingModality] = useState('all'); // all | gi | nogi
  const [categoryFilter, setCategoryFilter] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const mod = ['gi', 'nogi'].includes(rankingModality) ? rankingModality : undefined;
      const [c, a, t] = await Promise.all([
        championshipApi.get(id),
        championshipApi.getLiveRanking(id, mod),
        championshipApi.getTeamRanking(id, mod),
      ]);
      setChampionship(c.data);
      setAthletes(a.data || []);
      setTeams(t.data || []);
      setNotFound(false);
    } catch (e) {
      setChampionship(null);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id, rankingModality]);

  useEffect(() => { load(); }, [load]);

  // Ranking separado por modalidade (combine=false) começa em Kimono.
  useEffect(() => {
    if (championship?.modality === 'misto' &&
        championship?.combine_modality_ranking === false &&
        rankingModality === 'all') {
      setRankingModality('gi');
    }
  }, [championship, rankingModality]);

  const isMisto = championship?.modality === 'misto';
  const combineRanking = championship?.combine_modality_ranking !== false;
  const modalityOptions = combineRanking
    ? [{ value: 'all', label: 'Todas as modalidades' }, { value: 'gi', label: 'Kimono' }, { value: 'nogi', label: 'No-Gi' }]
    : [{ value: 'gi', label: 'Kimono' }, { value: 'nogi', label: 'No-Gi' }];

  const categories = useMemo(
    () => Array.from(new Set(athletes.map((a) => a.category_name).filter(Boolean))).sort(),
    [athletes],
  );

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Todas as categorias' },
      ...categories.map((c) => ({ value: c, label: c })),
    ],
    [categories],
  );

  const groupedAthletes = useMemo(() => {
    const filtered = categoryFilter ? athletes.filter((a) => a.category_name === categoryFilter) : athletes;
    const map = new Map();
    for (const r of filtered) {
      const key = r.category_name || 'Sem categoria';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    for (const rows of map.values()) {
      rows.sort((a, b) => (a.place || 99) - (b.place || 99));
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [athletes, categoryFilter]);

  const sortedTeams = useMemo(
    () => [...teams].sort(
      (a, b) => b.gold - a.gold || b.silver - a.silver || b.bronze - a.bronze
        || b.total_points - a.total_points
        || (a.team_name || '').localeCompare(b.team_name || ''),
    ),
    [teams],
  );

  if (loading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span>
        </div>
      </PageContent>
    );
  }

  if (notFound || !championship) {
    return (
      <PageContent>
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent>
            <EstadoVazio
              icone={Trophy}
              titulo="Campeonato não encontrado"
              descricao="Verifique o campeonato selecionado no menu lateral."
            />
          </CardContent>
        </Card>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <div className="space-y-6" data-testid="championship-ranking-page">
        {/* Header — mesmo padrão das demais páginas do campeonato */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Chivo']">Ranking do Campeonato</h1>
            <p className="text-[#A1A1AA] mt-1">
              {championship.name}
              {championship.date ? ` · ${championship.date}` : ''}
            </p>
          </div>
          {isMisto && (
            <div className="w-full sm:w-[240px]">
              <Label className="text-[#A1A1AA]">Modalidade</Label>
              <Select value={rankingModality} onValueChange={setRankingModality}>
                <SelectTrigger
                  data-testid="championship-ranking-modality"
                  className="bg-[#141414] border-[#27272A] text-white mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  {modalityOptions.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-white hover:bg-[#27272A]">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Tabs defaultValue="athletes" className="w-full">
          <TabsList className="bg-[#1A1A1A] border border-[#27272A] p-1 mb-4">
            <TabsTrigger
              value="athletes"
              data-testid="championship-ranking-tab-athletes"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]"
            >
              <Users className="w-4 h-4 mr-2" />Atletas
            </TabsTrigger>
            <TabsTrigger
              value="teams"
              data-testid="championship-ranking-tab-teams"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black text-[#A1A1AA]"
            >
              <Shield className="w-4 h-4 mr-2" />Equipes
            </TabsTrigger>
          </TabsList>

          {/* ============ ATLETAS ============ */}
          <TabsContent value="athletes">
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D4AF37]" />
                  Ranking de Atletas
                </CardTitle>
                {categories.length > 1 && (
                  <div className="w-full sm:w-72">
                    <SearchableCombobox
                      value={categoryFilter}
                      onChange={setCategoryFilter}
                      options={categoryOptions}
                      placeholder="Todas as categorias"
                      searchPlaceholder="Buscar categoria..."
                      testId="championship-ranking-category"
                      keywords={(o) => normalizeForSearch(o.label)}
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {athletes.length === 0 ? (
                  <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
                    <Medal className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    Nenhum resultado registrado neste campeonato ainda
                  </div>
                ) : groupedAthletes.length === 0 ? (
                  <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
                    <Medal className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    Nenhum atleta nesta categoria
                  </div>
                ) : (
                  <div className="space-y-6">
                    {groupedAthletes.map(([category, rows]) => (
                      <div key={category}>
                        <div className="flex items-baseline gap-2 mb-3 border-b border-[#27272A] pb-1">
                          <Medal className="w-4 h-4 self-center text-[#D4AF37]" />
                          <h3 className="text-lg font-bold text-white font-['Chivo']">{category}</h3>
                          <span className="text-xs text-[#71717A]">
                            {rows.length} {rows.length === 1 ? 'colocado' : 'colocados'}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {rows.map((r) => (
                            <div
                              key={`${r.bracket_id}-${r.athlete_id}`}
                              data-testid={`championship-ranking-athlete-${r.athlete_id}`}
                              className={`flex items-center gap-4 p-4 rounded-lg ${
                                ROW_TONE[r.place] || NEUTRAL_ROW
                              }`}
                            >
                              <PositionBadge place={r.place} />
                              <AthleteCell name={r.athlete_name} team={r.team_name} />
                              <PointsBlock points={r.points} />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ EQUIPES ============ */}
          <TabsContent value="teams">
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white font-['Chivo'] flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#D4AF37]" />
                  Ranking de Equipes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sortedTeams.length === 0 ? (
                  <div className="text-center py-16 text-[#71717A] border border-dashed border-[#27272A] rounded-xl">
                    <Shield className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    Nenhuma equipe com resultados neste campeonato ainda
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="championship-team-ranking">
                    {sortedTeams.map((team, i) => {
                      const place = i + 1;
                      return (
                        <div
                          key={team.team_id}
                          data-testid={`championship-ranking-team-${team.team_id}`}
                          className={`p-4 rounded-lg ${ROW_TONE[place] || NEUTRAL_ROW}`}
                        >
                          <div className="flex items-center gap-4">
                            <PositionBadge place={place} />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-white truncate">{team.team_name || 'Sem equipe'}</h3>
                              <div className="mt-1">
                                <MedalCounts gold={team.gold} silver={team.silver} bronze={team.bronze} />
                              </div>
                            </div>
                            <PointsBlock points={team.total_points} />
                          </div>
                          {(team.athletes || []).length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#27272A]">
                              {team.athletes.map((a, j) => (
                                <span
                                  key={`${a.name}-${a.place}-${j}`}
                                  className={`text-xs px-2 py-0.5 rounded bg-[#0A0A0A] border border-[#27272A] ${
                                    PLACE_TEXT[a.place] || 'text-[#A1A1AA]'
                                  }`}
                                >
                                  {a.name} - {PLACE_LABELS[a.place] || `${a.place}º`} ({a.category})
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PageContent>
  );
}
